/**
 * Espelha o schema novo do Firestore (`profiles/{uid}` + `students/{code}` +
 * `students/{code}/content/{curriculum|progress}`) — ver conectar.md, seção
 * "Nomenclatura final das coleções" e "DOCUMENTO B — SITE", tarefa S2.
 *
 * `kind` substitui o antigo `profileRole` como único filtro de visão do
 * dashboard: "managed" = aluno real (gerenciado por professor/pai); "individual"
 * = a própria pessoa (professor/pai/autônomo) usando o app pra si mesma,
 * tipicamente uso de capacitação — nunca entra nas métricas agregadas de aluno.
 */
export type StudentKind = 'managed' | 'individual';

/**
 * Modo de visão do dashboard — "todos" é o único que mistura tudo (agregado,
 * sem distinguir papel); as outras 3 nunca se misturam entre si nos totais.
 * "managed" é o `kind` de `RealProfile` direto (aluno real) — inclui também
 * perfis `kind: 'individual'` cujo `profileRole` resolvido é "filho"/"filha"
 * (aluno autônomo: mesma categoria de aluno(a) pro site, ver `matchesViewMode`
 * em `utils/profileFilter.ts`). "professor" e "pai_mae" são as outras 2
 * sub-visões do uso individual, resolvidas pelo `profileRole` do operador em
 * `profiles/{uid}/rooms/{roomId}` (via `RealProfile.writerRoomIds`) — o schema
 * não distingue isso no próprio `RealProfile`, só o `kind`.
 */
export type ProfileViewMode = 'todos' | 'managed' | 'professor' | 'pai_mae';

export const PROFILE_VIEW_MODE_LABELS: Record<ProfileViewMode, string> = {
  todos: 'Ver todos',
  managed: 'Aluno(a)',
  professor: 'Professor(a)',
  pai_mae: 'Pai/Mãe',
};

/** `profiles/{uid}` — quem opera o app (professor, pai/responsável, individual). */
export interface OperatorProfile {
  uid: string;
  displayName: string;
  avatarUrl?: string | null;
  /** Hint de UI (ex.: qual tela abrir por padrão) — nunca populado com o papel real, ver `profileRole`. */
  defaultRole: 'professor' | 'pai' | 'mae' | 'individual' | string;
  /** Papel real da conta ("professor"/"pai"/"mae"/"filho"/"filha") — mesmo campo/valores
   *  que `users/{userId}.profileRole` tinha no schema legado. Usado pra resolver a visão
   *  (Professor(a)/Aluno(a)/Pai-Mãe) de perfis `kind: 'individual'` — ver `profileFilter.ts`. */
  profileRole?: 'professor' | 'pai' | 'mae' | 'filho' | 'filha' | string;
  schoolId?: string;
  createdAt: number;
  updatedAt: number;
}

export interface CategoryDoc {
  id: string;
  categoryId: string;
  name: string;
  type: 'DEFAULT' | 'USER_CREATED';
  createdAt: number;
  updatedAt: number;
}

export interface SubcategoryDoc {
  id: string;
  subCategoryId: number;
  name: string;
  categoryName: string;
  categoryId: string;
  createdAt: number;
  updatedAt: number;
}

export interface SubcategoryClickDoc {
  id: string;
  categoryName: string;
  subcategoryName: string;
  clickCount: number;
  updatedAt: number;
}

export interface RoutineDoc {
  id: string;
  routineId: string;
  name: string;
  type: 'DEFAULT' | 'USER_CREATED';
  createdAt: number;
  updatedAt: number;
}

export interface SubRoutineDoc {
  id: string;
  subRoutineId: number;
  routineId: string;
  titulo: string;
  hora: string;
  data: string;
  tipoRepeticao: string;
  descricao: string;
  alarme: boolean;
  diasSemana: string;
  createdAt: number;
  updatedAt: number;
}

export interface ProgressStatsDoc {
  currentStreak: number;
  firstGameUseAt: number | null;
  drawingsRecognizedTotal: number;
  updatedAt: number;
}

export interface GameMetricDoc {
  id: string;
  gameId: string;
  correct: number;
  errors: number;
  attempts: number;
  timeSpentMs: number;
  firstUseAt: number;
  updatedAt: number;
}

export interface GameRoundMetricDoc {
  id: string;
  gameId: string;
  roundNumber: number;
  correctCount: number;
  errorCount: number;
  timeSpentMs: number;
  updatedAt: number;
}

export interface DrawingMetricDoc {
  id: string;
  label: string;
  count: number;
  createdAt: number;
  updatedAt: number;
}

export interface GenrePlayDoc {
  id: string;
  genre: string;
  playCount: number;
  updatedAt: number;
}

export interface MusicPlayDoc {
  id: string;
  title: string;
  artist: string;
  playCount: number;
  updatedAt: number;
}

export interface CardClickDoc {
  id: string;
  categoryName: string;
  cardName: string;
  clickCount: number;
  updatedAt: number;
}

/**
 * Um "aluno" (`students/{code}`) com o conteúdo consolidado já achatado —
 * o dashboard nunca vê a separação em `content/curriculum` × `content/progress`,
 * só este objeto único por aluno (nome mantido `RealProfile` pra não obrigar
 * renomear os ~10 arquivos de analytics que já consomem esse formato).
 */
export interface RealProfile {
  /** = código curto do aluno (doc ID de `students/{code}`). */
  id: string;
  name: string;
  age?: number;
  /** "masculino" | "feminino" | undefined (não informado) — ver `User.sex` no app. */
  sex?: string;
  kind: StudentKind;
  /** De qual schema este registro veio — "new" = `students/{code}` (só o
   *  aluno tem atividade própria registrada; professor/pai só aparecem como
   *  `kind: 'individual'` de "uso individual/capacitação", quase sempre sem
   *  atividade própria real). "legacy" = `users/{userId}` (schema antigo, tanto
   *  faz o papel — todo mundo registrava sua própria atividade igual). Motor
   *  de "o site se adapta às duas versões sem se reinventar": módulos de
   *  análise trocam o que mostram pra professor/pai baseado nisso, não num
   *  branch hardcoded — ver `contentManagementStatsFor` em `profileFilter.ts`. */
  source: 'legacy' | 'new';
  /** Só populado pra perfis `source: 'legacy'` — o schema antigo não tem o
   *  conceito de `profiles/{uid}/rooms/{roomId}` que resolve isso indiretamente
   *  pro schema novo (ver `individualProfileRole`), então vem direto do próprio
   *  doc (`users/{userId}.profileRole`). */
  profileRole?: 'professor' | 'pai' | 'mae' | 'filho' | 'filha' | string;
  /** uids de quem gerencia este aluno (professor/pai, ou o próprio dono se `kind === 'individual'`). */
  authorizedWriters: string[];
  /** Ids de `profiles/{uid}/rooms/{roomId}` vinculados como escritor — bate com
   *  `OperatorProfile.uid` (que, na prática, também é o roomId — ver conectar.md,
   *  adendo A13). Usado só pra resolver o `profileRole` (professor/pai/mãe) de
   *  perfis `kind: 'individual'` — ver `utils/profileFilter.ts`. */
  writerRoomIds: string[];
  createdAt: number;
  updatedAt: number;
  categories: CategoryDoc[];
  subcategories: SubcategoryDoc[];
  subcategoryClicks: SubcategoryClickDoc[];
  routines: RoutineDoc[];
  subRoutines: SubRoutineDoc[];
  progressStats?: ProgressStatsDoc;
  gameMetrics: GameMetricDoc[];
  gameRoundMetrics: GameRoundMetricDoc[];
  drawingMetrics: DrawingMetricDoc[];
  genrePlays: GenrePlayDoc[];
  musicPlays: MusicPlayDoc[];
  cardClicks: CardClickDoc[];
}

/**
 * Rótulo de atividade recente (tarefa S3) — derivado de quando o currículo/
 * progresso de um aluno *gerenciado* mudou de versão, cruzado com `profiles`
 * pra nomear quem gerencia. Não existe atribuição por item (o app não grava
 * `createdBy` por categoria/rotina — ver nota da tarefa S3 em conectar.md),
 * então o rótulo é por aluno + tipo de conteúdo, não por item individual.
 */
export interface ActivityEntry {
  id: string;
  studentId: string;
  studentName: string;
  content: 'curriculum' | 'progress';
  /** Nomes de exibição de quem está em `authorizedWriters` no momento da mudança. */
  actorNames: string[];
  at: number;
}

export interface ProfilesSyncResponse {
  profiles: RealProfile[];
  operators: OperatorProfile[];
  activity: ActivityEntry[];
}
